import {
  HStack,
  LucideIcon,
  IconButton,
  Grid,
  Image,
  VStack,
  Carousel,
  Flex,
  type CarouselApi,
} from '@skin-seoul/design-system';

import { AnimatePresence, motion } from 'motion/react';
import { StarIcon } from '../assets/icons/StarIcon';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Text, useBreakpointValue } from '@skin-seoul/design-system';
import type { ReviewImage, Review } from '..';
import { formatRelativeTime } from '@skin-seoul/service/src/utils/review/dateUtils';
import { ReviewImageDetailModal } from './ReviewImageDetailModal';
import avatar from '../assets/images/avatar.png';
interface DragInfo {
  offset: {
    x: number;
    y: number;
  };
  velocity: {
    x: number;
    y: number;
  };
}

const MotionBox = motion.create(Box);
const Overlay = ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
  <MotionBox
    position="fixed"
    top={0}
    left={0}
    right={0}
    bottom={0}
    backgroundColor="rgba(0, 0, 0, 0.7)"
    zIndex={999998}
    display="flex"
    alignItems="center"
    justifyContent="center"
    {...props}
  >
    {children}
  </MotionBox>
);

const ModalContainer = ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
  <Box
    position="relative"
    width="90vw"
    maxWidth="1000px"
    maxHeight="90dvh"
    background="white"
    borderRadius="20px"
    zIndex={999999}
    overflow="hidden"
    display="flex"
    flexDirection="column"
    {...props}
  >
    {children}
  </Box>
);

const MobileDrawer = ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
  <MotionBox
    position="fixed"
    left={0}
    right={0}
    bottom={0}
    maxHeight="90dvh"
    background="white"
    borderRadius="20px 20px 0 0"
    zIndex={999999}
    overflow="hidden"
    display="flex"
    flexDirection="column"
    css={{ touchAction: 'none' }}
    {...props}
  >
    {children}
  </MotionBox>
);

const EmptyState = ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    height="200px"
    color="#666"
    textAlign="center"
    {...props}
  >
    {children}
  </Box>
);

const EmptyStateText = ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
  <Text as="p" css={{ fontSize: '16px' }} margin={0} fontFamily="'Plus Jakarta Sans', sans-serif" {...props}>
    {children}
  </Text>
);

interface ImageGalleryContentProps {
  images: ReviewImage[];
  onImageClick: (image: ReviewImage) => void;
}

const ImageGalleryContent = ({ images, onImageClick }: ImageGalleryContentProps) => {
  const isMobile = useBreakpointValue({ base: true, tablet: false });

  if (images.length === 0) {
    return (
      <EmptyState>
        <EmptyStateText>No customer images yet</EmptyStateText>
      </EmptyState>
    );
  }

  return (
    <Grid
      gridTemplateColumns="repeat(2, 1fr)"
      gap={isMobile ? 3 : '12px'}
      paddingTop={isMobile ? '0px' : '32px'}
      paddingBottom="24px"
    >
      {images.map((image, index) => (
        <Image
          borderRadius="12px"
          key={image.id || index}
          src={image.url}
          alt={image.alt || `Customer image ${index + 1}`}
          loading={index < 6 ? 'eager' : 'lazy'}
          aspectRatio={isMobile ? '158 / 235' : '470 / 235'}
          w="full"
          objectFit="cover"
          cursor="pointer"
          onClick={() => onImageClick(image)}
          onError={(_e: React.SyntheticEvent<HTMLImageElement, Event>) => {
            console.error(`Failed to load image: ${image.url}`);
          }}
        />
      ))}
    </Grid>
  );
};

interface ReviewDetailContentProps {
  review: Review;
  selectedImageId?: string;
  onImageClick: (image: ReviewImage) => void;
}

const ReviewDetailContent = ({ review, selectedImageId, onImageClick }: ReviewDetailContentProps) => {
  const reviewImages = useMemo(() => review.images || [], [review]);
  const hasReviewImages = Boolean(review.images && review.images.length > 0);
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();

  const scrollToImage = useCallback(
    (imageId: string) => {
      if (isMobile) {
        const element = document.getElementById(`review-image-${imageId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
      } else if (carouselApi) {
        const imageIndex = reviewImages.findIndex((image) => image.id === imageId);
        if (imageIndex !== -1) {
          carouselApi.scrollTo(imageIndex);
        }
      }
    },
    [isMobile, carouselApi, reviewImages],
  );

  useEffect(() => {
    if (selectedImageId) {
      const image = reviewImages.find((image) => image.id === selectedImageId);
      if (image) {
        scrollToImage(selectedImageId);
      }
    }
  }, [selectedImageId, reviewImages, scrollToImage]);

  return (
    <VStack gap={4} align="start" w="full">
      {hasReviewImages && (
        <>
          {isMobile ? (
            <HStack
              gap={3}
              w="full"
              overflowX="auto"
              css={{
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
              }}
            >
              {reviewImages.map((image) => (
                <Image
                  key={image.id}
                  id={`review-image-${image.id}`}
                  src={image.url}
                  alt={image.alt}
                  borderRadius="12px"
                  aspectRatio="1"
                  objectFit="cover"
                  w={reviewImages.length === 1 ? '100%' : '70vw'}
                  h={reviewImages.length === 1 ? '100%' : '70vw'}
                  flexShrink={0}
                  cursor="pointer"
                  onClick={() => onImageClick(image)}
                />
              ))}
            </HStack>
          ) : (
            <Box w="full" h="456px">
              <Carousel
                opts={{
                  align: 'start',
                  loop: true,
                }}
                setApi={setCarouselApi}
              >
                <Carousel.Content>
                  {reviewImages.map((image) => (
                    <Carousel.Item key={image.id}>
                      <Flex h="456px" align="center" justify="center" bg="generic.0">
                        <Image
                          id={`review-image-${image.id}`}
                          src={image.url}
                          alt={image.alt}
                          borderRadius="12px"
                          objectFit="contain"
                          h="456px"
                          cursor="pointer"
                          onClick={() => onImageClick(image)}
                        />
                      </Flex>
                    </Carousel.Item>
                  ))}
                </Carousel.Content>
                <Carousel.Previous bg="neutral.50" w="48px" h="48px" borderRadius="50%" left="0px" border="none">
                  <LucideIcon name="ChevronLeft" w="36px" h="36px" color="neutral.400" />
                </Carousel.Previous>
                <Carousel.Next bg="neutral.50" w="48px" h="48px" borderRadius="50%" border="none" right="0px">
                  <LucideIcon name="ChevronRight" w="36px" h="36px" color="neutral.400" />
                </Carousel.Next>
              </Carousel>
            </Box>
          )}
        </>
      )}
      <VStack gap={4} align="start" w="full">
        {!isMobile && (
          <HStack justify="space-between" align="center" w="full">
            <HStack gap={1} align="center">
              <Text textStyle="heading.h5" fontWeight="semibold" color="primary.400">
                {review.rating}
              </Text>
              <StarIcon width={20} height={20} />
            </HStack>
            <Text color="neutral.400" textStyle="label.small" fontWeight="normal">
              {formatRelativeTime(review.createdAt)}
            </Text>
          </HStack>
        )}

        <VStack align="start" gap={3} w="full">
          <Text textStyle="paragraph.large" fontWeight="semibold" color="neutral.800">
            {review.title}
          </Text>
          <Text textStyle="paragraph.small" fontWeight="normal" color="neutral.500">
            {review.content}
          </Text>
        </VStack>

        <HStack borderRadius="8px" p={3} bg="neutral.50" w="full" gap={3}>
          <Image w={8} h={8} borderRadius="50%" src={avatar.src} alt="avatar" flexShrink={0} />
          <VStack align="start" gap={1}>
            <Text textStyle="label.small" fontWeight="medium" color="neutral.800">
              {review.author.name}
            </Text>
            <HStack gap={1} flexWrap="wrap">
              {review.skinType.map((type, index) => (
                <Text key={index} textStyle="label.xsmall" fontWeight="normal" color="neutral.700">
                  {type}
                  {index < review.skinType.length - 1 && ' •'}
                </Text>
              ))}
            </HStack>
          </VStack>
        </HStack>
      </VStack>
    </VStack>
  );
};

interface ReviewDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: ReviewImage[];
  reviews: Review[];
  initialSelectedReview?: Review | null;
  initialSelectedImageId?: string | null;
}

const ReviewDetailModal = ({
  isOpen,
  onClose,
  images,
  reviews,
  initialSelectedReview = null,
  initialSelectedImageId = null,
}: ReviewDetailModalProps) => {
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  const [selectedReview, setSelectedReview] = useState<Review | null>(initialSelectedReview);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(initialSelectedImageId);
  const [imageDetailModalOpen, setImageDetailModalOpen] = useState(false);
  const [selectedImageForDetail, setSelectedImageForDetail] = useState<ReviewImage | null>(null);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Set initial values when modal opens
      setSelectedReview(initialSelectedReview);
      setSelectedImageId(initialSelectedImageId);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, initialSelectedReview, initialSelectedImageId]);

  const handleImageClick = (image: ReviewImage) => {
    const review = reviews.find((r) => r.id === image.reviewId);
    if (review) {
      setSelectedReview(review);
      setSelectedImageId(image.id);
    }
  };

  const handleImageDetailClick = (image: ReviewImage) => {
    setSelectedImageForDetail(image);
    setImageDetailModalOpen(true);
  };

  // 현재 선택된 리뷰의 이미지들과 클릭된 이미지의 인덱스를 계산
  const reviewImages = selectedReview?.images || [];
  const selectedImageIndex = selectedImageForDetail
    ? reviewImages.findIndex((img) => img.id === selectedImageForDetail.id)
    : 0;

  const handleImageDetailClose = () => {
    setImageDetailModalOpen(false);
    setSelectedImageForDetail(null);
  };

  // 제목을 조건부로 결정
  const modalTitle = selectedReview ? 'Review Detail' : 'Images from reviews';

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: DragInfo) => {
    if (info.offset.y > 200 || info.velocity.y > 200) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {isMobile ? (
            <>
              <Overlay
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={handleOverlayClick}
              />
              <MobileDrawer
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 500 }}
                drag="y"
                dragConstraints={{ top: 0, bottom: 0 }}
                onDragEnd={handleDragEnd}
                padding="24px 16px 16px"
              >
                <HStack w="full" justify="space-between" align="center">
                  <Text textStyle="heading.h6.mobile" fontWeight="normal">
                    {modalTitle}
                  </Text>
                  <IconButton onClick={onClose} aria-label="Close modal">
                    <LucideIcon name="X" w="24px" h="24px" color="generic.100" />
                  </IconButton>
                </HStack>

                <Box
                  flex={1}
                  overflowY="auto"
                  paddingTop="16px"
                  css={{
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                  }}
                >
                  {selectedReview ? (
                    <ReviewDetailContent
                      review={selectedReview}
                      selectedImageId={selectedImageId || undefined}
                      onImageClick={handleImageDetailClick}
                    />
                  ) : (
                    <ImageGalleryContent images={images} onImageClick={handleImageClick} />
                  )}
                </Box>
              </MobileDrawer>
            </>
          ) : (
            <Overlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleOverlayClick}
            >
              <ModalContainer
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ type: 'spring', damping: 25, stiffness: 500 }}
                padding="24px 24px 0px"
              >
                <HStack w="full" justify="space-between" align="center">
                  <Text textStyle="heading.h6.mobile" fontWeight="semibold">
                    {modalTitle}
                  </Text>
                  <IconButton onClick={onClose} aria-label="Close modal">
                    <LucideIcon name="X" w="24px" h="24px" color="generic.100" />
                  </IconButton>
                </HStack>

                <Box
                  flex={1}
                  overflowY="auto"
                  css={{
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#c1c1c1',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: '#a8a8a8',
                    },
                  }}
                >
                  {selectedReview ? (
                    <Box paddingTop="32px" paddingBottom="24px">
                      <ReviewDetailContent
                        review={selectedReview}
                        selectedImageId={selectedImageId || undefined}
                        onImageClick={handleImageDetailClick}
                      />
                    </Box>
                  ) : (
                    <ImageGalleryContent images={images} onImageClick={handleImageClick} />
                  )}
                </Box>
              </ModalContainer>
            </Overlay>
          )}
        </>
      )}

      <ReviewImageDetailModal
        isOpen={imageDetailModalOpen}
        onClose={handleImageDetailClose}
        image={selectedImageForDetail}
        images={reviewImages}
        initialIndex={selectedImageIndex >= 0 ? selectedImageIndex : 0}
      />
    </AnimatePresence>
  );
};

export default ReviewDetailModal;
